/**
 * Compare two resolved sort values. Numbers compare numerically; anything else
 * compares as strings via `localeCompare`, normalized to -1/0/1 with `Math.sign`
 * so callers can apply direction uniformly. `localeCompare` (over `<`) keeps
 * mixed-case/punctuated URL ordering stable.
 */
export function compareSortValues(av: number | string, bv: number | string): number {
  if (typeof av === 'number' && typeof bv === 'number') {
    if (av === bv) {
      return 0;
    }
    return av < bv ? -1 : 1;
  }
  return Math.sign(String(av).localeCompare(String(bv)));
}

/**
 * Generic, multi-key, stable in-memory row sort. Sort keys apply in priority
 * order: the first key that distinguishes two rows wins, and fully-tied rows
 * keep their original order (`Array.prototype.sort` is stable). `getValue`
 * resolves a row + column id to a comparable value; `desc` inverts per key. The
 * input array is not mutated.
 */
export function sortRows<T>(
  rows: T[],
  sort: Array<{ id: string; desc?: boolean }>,
  getValue: (row: T, id: string) => number | string,
): T[] {
  if (sort.length === 0) {
    return rows;
  }
  return [...rows].sort((a, b) => {
    for (const { id, desc = false } of sort) {
      const cmp = compareSortValues(getValue(a, id), getValue(b, id));
      if (cmp !== 0) {
        return desc ? -cmp : cmp;
      }
    }
    return 0;
  });
}
